import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await register(form.email, form.password, form.name);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-neutral-50">
      <div className="w-full max-w-md bg-white border border-neutral-200 rounded-sm p-8" data-testid="register-card">
        <div className="font-display text-2xl font-black tracking-tight flex items-center gap-2 mb-6">
          <span className="inline-block w-3 h-3 bg-yellow-400" /> ORBIT
        </div>
        <div className="mb-6">
          <div className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">New account</div>
          <h2 className="font-display text-3xl font-bold tracking-tight">Start building.</h2>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
          <div>
            <Label htmlFor="name" className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">Name</Label>
            <Input id="name" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 rounded-sm h-11" data-testid="register-name-input" />
          </div>
          <div>
            <Label htmlFor="email" className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">Email</Label>
            <Input id="email" type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1 rounded-sm h-11" data-testid="register-email-input" />
          </div>
          <div>
            <Label htmlFor="password" className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">Password (min 6)</Label>
            <Input id="password" type="password" required value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-1 rounded-sm h-11" data-testid="register-password-input" />
          </div>
          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm p-3" data-testid="register-error">{error}</div>
          )}
          <Button type="submit" disabled={busy}
            className="w-full h-11 rounded-sm bg-neutral-950 hover:bg-neutral-800 text-white font-semibold"
            data-testid="register-submit-button">
            {busy ? "Creating…" : "Create account"}
          </Button>
        </form>

        <div className="mt-6 text-sm text-neutral-500">
          Have one?{" "}
          <Link to="/login" className="text-neutral-950 font-semibold underline underline-offset-4" data-testid="link-login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
